import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ArrowRight, ChevronRight } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import StatusBadge from "../../components/StatusBadge";
import api, { formatCurrency, formatDate, daysUntil } from "../../lib/api";
import { useTerms } from "../../lib/BrandContext";
import { withReturnTo } from "../../lib/backNavigation";

export default function Owed() {
  const { terms } = useTerms();
  const [records, setRecords] = useState(null);
  // Preserve last-known-good records across transient fetch failures. A
  // mobile hiccup during Home→Pay nav or PWA resume must NOT downgrade a
  // populated Pay screen back to the empty "Nothing outstanding" state.
  const [fetchError, setFetchError] = useState(false);
  // Same refetch-on-visibility contract as ParentDashboard so HOME and
  // PAY derive from an equally fresh snapshot after iOS bfcache restore,
  // PWA resume, or tab-visibility flips. Prevents the reverse of the
  // reported bug (stale Pay while Home is fresh).
  const load = useCallback(() => {
    return api.get("/payments")
      .then((r) => { setRecords(r.data); setFetchError(false); })
      .catch(() => {
        setRecords((prev) => (prev == null ? [] : prev));
        setFetchError(true);
      });
  }, []);
  useEffect(() => {
    load();
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    const onPageShow = (e) => {
      if (e.persisted) load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [load]);
  const owed = (records || []).filter((r) => r.status === "unpaid" || r.status === "rejected");

  if (records === null) {
    return (
      <AppLayout>
        <div className="flex h-64 items-center justify-center text-ink-500" data-testid="owed-loading">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div>
        <p className="label-caps">Parent</p>
        <h1 className="h2">Fees to Pay</h1>
        <p className="text-sm text-ink-500">Tap a fee to see how to pay.</p>
      </div>

      <div className="mt-5 space-y-3">
        {owed.map((r) => {
          const d = daysUntil(r.charge?.due_date);
          const tone = d != null && d < 0 ? "text-rose-600" : d != null && d <= 3 ? "text-amber-600" : "text-ink-700";
          return (
            <Link
              key={r.id}
              to={withReturnTo(`/parent/owed/${r.id}/pay`, { from: "/parent/owed", label: "Fees to Pay" })}
              data-testid={`owed-pay-${r.id}`}
              className="block rounded-2xl border border-ink-100 bg-white p-5 shadow-sm transition-colors active:bg-ink-100/60 sm:hover:bg-ink-100/40"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate font-heading text-lg font-bold text-ink-900">{r.charge?.title}</h3>
                    <StatusBadge status={r.status} />
                  </div>
                  <p className="mt-1 truncate text-sm text-ink-500">
                    {r.player?.first_name} {r.player?.last_name} · {r.team?.name}
                  </p>
                  <p className={`mt-1 text-xs font-semibold ${tone}`}>
                    Due {formatDate(r.charge?.due_date)}{" "}
                    {d == null ? "" : d < 0 ? `· ${Math.abs(d)}d overdue` : d === 0 ? "· today" : `· in ${d}d`}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-heading text-2xl font-bold text-ink-900">{formatCurrency(r.amount)}</p>
                  <span className="mt-1 inline-flex items-center text-xs font-bold text-brand">
                    How to Pay <ChevronRight className="ml-0.5 h-3 w-3" />
                  </span>
                </div>
              </div>
              {r.charge?.payment_instructions ? (
                <p className="mt-3 rounded-lg bg-ink-100/60 p-3 text-xs text-ink-700">
                  <span className="font-semibold text-ink-900">How to pay:</span> {r.charge.payment_instructions}
                </p>
              ) : null}
              {r.status === "rejected" && r.coach_note ? (
                <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                  <span className="font-semibold">{terms.leader} said:</span> {r.coach_note}
                </p>
              ) : null}
            </Link>
          );
        })}
        {owed.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-ink-200 bg-white p-10 text-center">
            <CalendarClock className="mx-auto h-8 w-8 text-ink-300" />
            <p className="mt-3 font-semibold text-ink-900">Nothing outstanding</p>
            <p className="text-sm text-ink-500">You're all caught up.</p>
          </div>
        ) : null}
      </div>
    </AppLayout>
  );
}
